import { isSetmoreBrand } from './brands';
import { getQueryParam, getUniqueUserIdFromQueryParam } from './common-helper';
import { sendSignupEvent } from './fetch-helper';
import getSessionInfo, { isMobileScreen } from './session-Info';

export const ANALYTICS_LITERAL = {
  SUCCESS: 'Success',
  FAILED: 'Failed',
  NEW_CUSTOMER: 'New customer',
  EXISTING_CUSTOMER: 'Existing customer',
};

const { brand, account, leadInfo } = getSessionInfo();

export const analyticsSignupTypeMap = {
  free: 'Free signup',
  paid: 'Paid signup',
  noCardSignup: 'Free Trial',
  upgrade: 'Upgrade',
};

function getSetmoreGA4EventPayload({
  planInfo,
  accountId,
  amount,
  numberOfUsers,
  customerType,
  type
}) {
  const { price, id: planId, description: planName, planPeriod } = planInfo;

  if (type === analyticsSignupTypeMap.free) {
    type = 'Free';
  } else if (type === analyticsSignupTypeMap.paid) {
    type = 'Paid';
  } else {
    type = 'Upgrade';
  }

  const isAnnualPlan = planPeriod === 'yearly';
  return {
    customer: customerType,
    type,
    plan: planName,
    plan_type: isAnnualPlan ? 'Annual' : 'Monthly',
    amount,
    account: accountId || account?.id,
    brand: brand.name,
    event: 'purchase',
    ecommerce: {
      purchase: {
        transaction_id: `${accountId || account?.id}`,
        affiliation: customerType,
        value: amount,
        tax: '0',
        shipping: '0',
        currency: 'USD',
        coupon: '',
        items: [
          {
            item_name: planName,
            item_id: planId,
            item_price: isAnnualPlan ? parseFloat(price / 12).toFixed(2) : price,
            item_brand: brand.name,
            item_category: type,
            item_category_2:
              leadInfo?.authorized && leadInfo?.authType !== 'REGULAR'
                ? leadInfo?.authType
                : 'email',
            item_category_3: customerType,
            item_category_4: isMobileScreen()
              ? 'Mobile_browser'
              : 'Desktop_browser',
            item_category_5: getQueryParam('utm_source'),
            item_variant: '',
            quantity: numberOfUsers || 1,
          }
        ]
      }
    }
  };
}

function pushToDataLayer({
  status, planInfo, accountId, amount, message, numberOfUsers, customerType, contact, type,
}) {
  const {
    price, id: planId, description: planName, planPeriod,
  } = planInfo;

  const companyName = leadInfo?.companyName || contact?.companyName?.value;
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'Transaction',
      customer: customerType,
      type, // 'Upgrade/Free signup/Paid signup'
      plan: planName,
      plan_type: planPeriod,
      amount,
      status,
      account: `${accountId || account?.id}`,
      brand: brand.name,
      message: message || '',
      transactionId: `${companyName}_${accountId || account?.id}`,
      transactionAffiliation: 'Customers',
      transactionTotal: amount,
      transactionTax: 0,
      transactionShipping: 0,
      transactionProducts: [{
        sku: planId,
        name: `${companyName}_${accountId || account?.id}`,
        category: planPeriod,
        price,
        quantity: numberOfUsers,
      }],
    });
  }
}

function pushToG4DataLayer(params) {
  const {
    status,
    planInfo,
    accountId,
    amount,
    message,
    numberOfUsers,
    customerType,
    type,
    discount = ''
  } = params;
  
  const {
    price, id: planId, description: planName, planPeriod,
  } = planInfo;

 if (isSetmoreBrand) {
   window.dataLayer?.push(getSetmoreGA4EventPayload(params));
 } else {
   window.dataLayer?.push({
     customer: customerType,
     type,
     plan: planName,
     plan_type: planPeriod,
     amount,
     status,
     account: `${accountId || account?.id}`,
     brand: brand.name,
     message: message || '',
     company_key: `${accountId || account?.id}`,
     coupon: discount && `{discount?.id}_${discount?.description}`,
     event: 'purchase',
     ecommerce: {
       transaction_id: `${accountId || account?.id}`,
       affiliation: 'Customers',
       value: amount,
       tax: '0',
       shipping: '0',
       currency: 'USD',
       coupon: discount ? `${discount?.id}` : '',
       items: [
         {
           item_name: planName,
           item_id: `${accountId || account?.id}`,
           price,
           item_brand: '',
           item_category: planPeriod,
           item_category2: 'Customers',
           item_category3: planId,
           item_category4: type,
           item_category5: '',
           item_variant: '',
           quantity: numberOfUsers
         }
       ]
     }
   });
 }
}

export function trackFailure({
  planInfo = {},
  amount = 0,
  email = '',
  accountId = '',
  collatedUID = '',
  customerType = '',
  message = '',
  userFriendlyErrorMessage = '',
  numberOfUsers,
  type,
  comments,
}) {
  sendSignupEvent({
    status: type === 'Upgrade' ? 'Upgrade Failure' : 'Signup Failure',
    signupType: type,
    accountId: accountId || getUniqueUserIdFromQueryParam(),
    email,
    failureReason: message,
    amount,
    userErrorMessage: userFriendlyErrorMessage,
    comments,
  });

  if (isSetmoreBrand) return;

  pushToDataLayer({
    status: ANALYTICS_LITERAL.FAILED,
    planInfo,
    accountId: collatedUID,
    amount,
    message,
    numberOfUsers,
    customerType,
    type,
  });
  pushToG4DataLayer({
    status: ANALYTICS_LITERAL.FAILED,
    planInfo,
    accountId: collatedUID,
    amount,
    message,
    numberOfUsers,
    customerType,
    type,
  });
}

export function trackPageView({
  pageName,
  errorMessage,
  userFriendlyErrorMessage,
} = {}) {
  sendSignupEvent({
    status: 'Page Load',
    accountId: account?.id || getUniqueUserIdFromQueryParam(),
    email: leadInfo?.email,
    signupType: pageName,
    ...(errorMessage && { failureReason: errorMessage }),
    ...(userFriendlyErrorMessage && {
      userErrorMessage: userFriendlyErrorMessage,
    }),
  });

  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'pageview',
      brand: brand?.name,
      accountid: account?.id,
      type: pageName,
    });
  }
}

export function trackSuccess({
  amount,
  planDetails: { numberOfUsers },
  planInfo,
  customerType,
  accountId = '',
  contact,
  type,
  discount,
}) {
  pushToDataLayer({
    status: ANALYTICS_LITERAL.SUCCESS,
    planInfo,
    accountId,
    amount,
    message: 'success',
    numberOfUsers,
    customerType,
    contact,
    type,
  });
  pushToG4DataLayer({
    status: ANALYTICS_LITERAL.SUCCESS,
    planInfo,
    accountId,
    amount,
    message: 'success',
    numberOfUsers,
    customerType,
    type,
    discount,
  });
}

export function pushCustomEventToDataLayer({ event, data }) {
  if (window.dataLayer) {
    window.dataLayer.push({ event, ...data });
  }
}
